<template>

  <!-- Loader -->
  <LoadingDiv v-if="!isReady" />

  <div class="auth-start-shell sba-slide-in" v-else>
    <template v-if="manualAuthEnabled">
      <form action="#">
      <div class="auth-start-card">
      <div class="auth-start-main" :class="{ 'has-error': error }">
        <div class="auth-start-copy">
          <span>Step 1</span>
          <h6>Enrollee Details</h6>
          <p>Enter the enrollee identity exactly as it should appear on the authorization request.</p>
        </div>
        <div class="auth-start-fields">
          <div class="auth-start-field">
            <label class="auth-field-label" for="enrolleeId">Enrollee ID</label>
            <input class="auth-text-input" type="text" id="enrolleeId" v-model="enrolleeId" autocomplete="off" />
          </div>
          <div class="auth-start-field">
            <label class="auth-field-label" for="enrolleeName">Enrollee name</label>
            <input class="auth-text-input" type="text" id="enrolleeName" v-model="enrolleeName" autocomplete="off" />
          </div>
        </div>
        <div class="auth-start-error" v-if="error">
          <i class="material-icons">error_outline</i>
          <span>{{ error }}</span>
        </div>
      </div>
     
      <div class="auth-scheme-panel">
        <div class="auth-scheme-title">
          <span>Step 2</span>
          <h6>Scheme</h6>
        </div>
        <div class="auth-scheme-grid">
          <label class="auth-scheme-option" :class="{ 'is-selected': selectedScheme?.id === scheme.id }" v-for="scheme in schemes" :key="scheme.id">
            <input id="selectedScheme" v-model="selectedScheme" :value="scheme" type="radio" />
            <span>
              <strong>{{ scheme.name }}</strong>
              <small>{{ scheme.type == 'p' ? 'Private Scheme' : 'Social Scheme' }}</small>
            </span>
          </label>
        </div>
      </div>

      <div class="auth-start-submit">
        <button type="button" class="auth-next-button" :class="{
          disabled: !enrolleeId || !enrolleeName || !selectedScheme,
        }" @click="handleSearch()">
          <span>Next</span>
          <i class="material-icons" v-if="!isLoading">chevron_right</i>
        </button>
      </div>
      </div>
      </form>
    </template>

    <template v-else>
      <div class="auth-start-search-card">
        <form class="auth-search-form" @submit.prevent="handleSearch()">
          <div class="auth-search-main" :class="{ 'has-error': error }">
            <div class="auth-start-copy">
              <span>Step 1</span>
              <h6>Find Enrollee</h6>
              <p>Search the active enrollee register before creating a new authorization request.</p>
            </div>
            <div class="auth-search-control">
              <label class="auth-field-label" for="authEnrolleeSearch">Enrollee ID</label>
              <input
                class="auth-text-input"
                type="search"
                id="authEnrolleeSearch"
                v-model="enrolleeId"
                autocomplete="off"
                placeholder="Enter enrollee ID"
              />
            </div>
            <div class="auth-start-error" v-if="error">
              <i class="material-icons">error_outline</i>
              <span>{{ error }}</span>
            </div>
          </div>

          <div class="auth-start-submit">
            <button type="button" class="auth-clear-button" :class="{ disabled: isLoading || (!enrolleeId && !enrolleeGroup?.length) }"
              @click.prevent="handleClear">
              <i class="material-icons">restart_alt</i>
              <span>Clear</span>
            </button>
            <button type="submit" class="auth-next-button" :class="{ disabled: isLoading || !enrolleeId }">
              <span>{{ isLoading ? "Searching..." : "Search Register" }}</span>
              <i class="material-icons" v-if="!isLoading">search</i>
            </button>
          </div>
        </form>

        <div class="progress auth-start-progress" v-if="isLoading">
          <div class="indeterminate"></div>
        </div>

        <div class="auth-search-note" v-if="!enrolleeGroup?.length && !error && !isLoading">
          <i class="material-icons">verified_user</i>
          <span>Search and select an active enrollee from the register to continue.</span>
        </div>

        <!-- Confirmation for more than multiple enrollees -->
        <div class="auth-start-results" v-if="enrolleeGroup?.length">
          <button type="button" class="auth-start-result-card" v-for="enrollee in enrolleeGroup" :key="enrollee.id"
            @click.prevent="handleStart(enrollee)">
            <div class="auth-result-main">
              <span>{{ enrollee?.scheme.name.toUpperCase() }}</span>
              <h6>{{ capWords(enrollee?.name) }}</h6>
              <p>{{ enrollee?.enrollee_number }}</p>
            </div>
            <i class="material-icons">chevron_right</i>
          </button>
        </div>
      </div>
    </template>

  </div>

</template>

<script>
import { ref, watchEffect, onMounted } from "vue";
import { useStore } from "vuex";
import { capWordsFx } from "@/composables/fxLibrary/coreFx";
import getItems from "@/composables/getItems";
import getItemsOfItems from "@/composables/getItemsOfItems";
import { getCachedServerDate } from "@/composables/requestOptimization";
import { normalizeEnrolleeNumber, normalizeEnrolleeSearchValue } from "@/composables/enrolleeNumber";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { ID_NUMBER, addManualAuth } from '@/laravel/config';

export default {
  name: "StartAuthComponent",
  components: { LoadingDiv },
  emits: ["next"],
  setup(props, context) {
    const manualAuthEnabled = addManualAuth;
    const response = ref(null);
    const enrolleeGroup = ref(null);
    const enrolleeId = ref(null);
    const enrolleeName = ref(null);
    const selectedScheme = ref(null);
    const serverDate = ref(null);
    const error = ref(false);
    const isReady = ref(!manualAuthEnabled);
    const isLoading = ref(false);

    // store
    const store = useStore();


    // display
    onMounted(() => {
      // get standalone server date
      if (manualAuthEnabled) {
        (async () => {
        const { res } = await getCachedServerDate(getItems);

        if (res.value) {
          serverDate.value = res.value.data.current_date_full;
        }
        // open page
        isReady.value = true;
      })();

      }
      
    });


    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // get function
    const getSearchItemsFromApi = (table, match) => {
      // loading starts
      isLoading.value = true;
      // clear error
      error.value = null;
      enrolleeGroup.value = [];
      response.value = null;

      (async () => {
        const { res } = await getItemsOfItems(table, match);

        if (res.value && res.value.status && res.value.data?.length) {
          // set server date
          serverDate.value = res.value.server;
          // display options
          if (res.value.data.length === 1) {
            response.value = res.value.data[0];
          } else {
            enrolleeGroup.value = res.value.data;
          }
        } else {
          error.value = `Enrollee not on current List!`;
          response.value = null; // clear standing response
        }

        // page is ready, loading is complete
        isLoading.value = false;
      })();
    };

    // search
    const handleSearch = () => {

      if (!manualAuthEnabled) {
        if (!enrolleeId.value?.trim()) {
          error.value = "Enter an enrollee ID to search.";
          return;
        }

        // loading starts
        isLoading.value = true;
        // val
        let val = `q=${encodeURIComponent(normalizeEnrolleeSearchValue(enrolleeId.value))}`;
        // search: 0 = provider_id
        getSearchItemsFromApi("operator/enrollee/encounter/checker", `0?${val}`);


      } else {
        if (!enrolleeId.value || !enrolleeName.value || !selectedScheme.value) {
          error.value = "Enter enrollee ID, enrollee name, and scheme.";
          return;
        }

        // build pseudo enrollee
        let newVal = {
          id: Date.now(),
          payer_id: ID_NUMBER,
          name: enrolleeName.value,
          enrollee_number: normalizeEnrolleeNumber(enrolleeId.value),
          dob: null,
          sex: null,
          client_name: null,
          scheme: selectedScheme.value,
          scheme_id: selectedScheme.value?.id,
          profile_img: null,
        };

        // push start
        handleStart(newVal);

      }

    };

    // start
    const handleStart = (val) => {
      // set
      response.value = val;
    }

    // clear
    const handleClear = () => {
      enrolleeGroup.value = [];
      enrolleeId.value = null;
      enrolleeName.value = null;
      response.value = null;
      error.value = false;
    };

    // next
    watchEffect(() => {
      if (response.value) {
        context.emit("next", {
          value: response.value,
          serverDate: serverDate.value,
        });
      }
    });

    return {
      capWords,
      handleStart,
      handleSearch,
      handleClear,
      error,
      isReady,
      isLoading,
      enrolleeGroup,
      enrolleeId,
      enrolleeName,
      selectedScheme,
      manualAuthEnabled,
      schemes: store.state.user.schemes
    };
  },
};
</script>

<style scoped>
.auth-start-shell {
  margin: 0;
  min-width: 0;
}

.auth-start-card,
.auth-start-search-card {
  background: var(--ui-background);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  box-shadow: none;
  overflow: hidden;
}

.auth-start-main,
.auth-search-main {
  align-items: start;
  background: transparent;
  display: grid;
  gap: clamp(1.5rem, 4vw, 4rem);
  grid-template-columns: minmax(13rem, 0.36fr) minmax(0, 1fr);
  padding: 1.5rem;
}

.auth-start-copy {
  padding-top: 0.1rem;
}

.auth-start-copy > span,
.auth-scheme-title > span {
  color: var(--ui-primary);
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1;
  margin-bottom: 0.55rem;
  text-transform: uppercase;
}

.auth-start-copy h6,
.auth-scheme-title h6 {
  color: var(--ui-foreground);
  font-family: var(--ui-font-display);
  font-size: 1rem;
  font-weight: 650;
  line-height: 1.35;
  margin: 0;
}

.auth-start-copy p {
  color: var(--ui-muted-foreground);
  font-size: 0.8125rem;
  line-height: 1.55;
  margin: 0.45rem 0 0;
  max-width: 29rem;
}

.auth-start-fields {
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(12rem, 0.8fr) minmax(16rem, 1.2fr);
}

.auth-start-field,
.auth-search-control {
  min-width: 0;
}

.auth-field-label {
  color: var(--ui-foreground);
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0 0 0.45rem;
  position: static;
  transform: none;
}

.auth-text-input {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-input) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  box-shadow: none !important;
  box-sizing: border-box !important;
  color: var(--ui-foreground);
  font-family: var(--ui-font-sans);
  font-size: 0.875rem;
  font-weight: 500;
  height: 2.85rem !important;
  margin: 0 !important;
  padding: 0 0.8rem !important;
  width: 100% !important;
}

.auth-text-input::placeholder {
  color: var(--ui-muted-foreground);
  font-weight: 400;
}

.auth-text-input:focus {
  border-color: var(--ui-ring) !important;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ui-ring) 20%, transparent) !important;
  outline: none;
}

.auth-start-error {
  align-items: center;
  background: color-mix(in srgb, var(--ui-destructive) 7%, var(--ui-background));
  border: 1px solid color-mix(in srgb, var(--ui-destructive) 24%, var(--ui-border));
  border-radius: calc(var(--ui-radius) - 2px);
  color: var(--ui-destructive);
  display: flex;
  font-size: 0.75rem;
  font-weight: 600;
  gap: 0.5rem;
  grid-column: 2;
  padding: 0.7rem 0.75rem;
}

.auth-start-error i {
  font-size: 1rem;
}

.auth-scheme-panel {
  background: color-mix(in srgb, var(--ui-muted) 35%, transparent);
  border-top: 1px solid var(--ui-border);
  display: grid;
  gap: clamp(1.5rem, 4vw, 4rem);
  grid-template-columns: minmax(13rem, 0.36fr) minmax(0, 1fr);
  padding: 1.25rem 1.5rem;
}

.auth-scheme-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
}

.auth-scheme-option {
  align-items: flex-start;
  background: var(--ui-background);
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 2px);
  cursor: pointer;
  display: flex;
  gap: 0.65rem;
  min-height: 4rem;
  padding: 0.85rem;
}

.auth-scheme-option.is-selected {
  background: color-mix(in srgb, var(--ui-primary) 6%, var(--ui-background));
  border-color: var(--ui-primary);
}

.auth-scheme-option input {
  margin-top: 0.2rem;
}

.auth-scheme-option span {
  color: var(--ui-foreground);
  line-height: 1.3;
}

.auth-scheme-option strong {
  display: block;
  font-size: 0.8125rem;
  font-weight: 650;
}

.auth-scheme-option small {
  color: var(--ui-muted-foreground);
  display: block;
  font-size: 0.72rem;
  margin-top: 0.25rem;
}

.auth-start-submit {
  align-items: center;
  background: transparent;
  border-top: 1px solid var(--ui-border);
  display: flex;
  gap: 0.65rem;
  justify-content: flex-end;
  padding: 1rem 1.5rem;
}

.auth-next-button,
.auth-clear-button {
  align-items: center;
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  cursor: pointer;
  display: inline-flex;
  font-family: var(--ui-font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.45rem;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 0.9rem;
}

.auth-next-button {
  background: var(--ui-primary);
  border: 1px solid var(--ui-primary);
  color: var(--ui-primary-foreground);
}

.auth-clear-button {
  background: var(--ui-background);
  border: 1px solid var(--ui-border);
  color: var(--ui-foreground);
}

.auth-next-button:hover,
.auth-clear-button:hover {
  filter: brightness(0.97);
}

.auth-next-button:focus-visible,
.auth-clear-button:focus-visible,
.auth-start-result-card:focus-visible {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.auth-next-button i,
.auth-clear-button i {
  color: inherit;
  font-size: 1rem;
}

.auth-next-button.disabled,
.auth-clear-button.disabled {
  background: var(--ui-muted);
  border-color: var(--ui-border);
  color: var(--ui-muted-foreground);
  cursor: not-allowed;
  pointer-events: none;
}

.auth-start-progress {
  border-radius: 0;
  height: 2px;
  margin: -2px 0 0;
}

.auth-search-note {
  align-items: center;
  background: color-mix(in srgb, var(--ui-muted) 32%, transparent);
  border-top: 1px solid var(--ui-border);
  color: var(--ui-muted-foreground);
  display: flex;
  font-size: 0.75rem;
  gap: 0.6rem;
  line-height: 1.45;
  padding: 0.85rem 1.5rem;
}

.auth-search-note i {
  color: var(--ui-primary);
  font-size: 1rem;
}

.auth-start-results {
  background: transparent;
  border-top: 1px solid var(--ui-border);
  display: grid;
  padding: 0;
}

.auth-start-result-card {
  align-items: center;
  background: var(--ui-background);
  border: 0;
  border-bottom: 1px solid var(--ui-border);
  border-radius: 0;
  box-shadow: none;
  color: var(--ui-foreground);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  text-align: left;
}

.auth-start-result-card:last-child {
  border-bottom: 0;
}

.auth-start-result-card:hover {
  background: var(--ui-accent);
}

.auth-result-main {
  min-width: 0;
}

.auth-result-main span {
  color: var(--ui-muted-foreground);
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 0.4rem;
  text-transform: uppercase;
}

.auth-result-main h6 {
  color: var(--ui-foreground);
  font-size: 0.875rem;
  font-weight: 650;
  margin: 0;
  overflow-wrap: anywhere;
}

.auth-result-main p {
  color: var(--ui-muted-foreground);
  font-size: 0.75rem;
  font-weight: 500;
  margin: 0.3rem 0 0;
}

.auth-start-result-card > i {
  color: var(--ui-muted-foreground);
  flex: 0 0 auto;
  font-size: 1.1rem;
}

@media (max-width: 800px) {
  .auth-start-main,
  .auth-search-main,
  .auth-scheme-panel,
  .auth-start-fields {
    gap: 1.25rem;
    grid-template-columns: 1fr;
  }

  .auth-start-error {
    grid-column: auto;
  }
}

@media (max-width: 600px) {
  .auth-start-main,
  .auth-search-main {
    padding: 1.1rem;
  }

  .auth-start-submit {
    align-items: stretch;
    flex-direction: column-reverse;
    padding: 1rem 1.1rem;
  }

  .auth-next-button,
  .auth-clear-button {
    width: 100%;
  }

  .auth-search-note,
  .auth-start-result-card {
    padding-inline: 1.1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-start-card,
  .auth-start-search-card,
  .auth-start-result-card {
    animation: none;
  }
}
</style>